import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  AutocompleteComponent,
  DatePickerComponent,
  DialogComponent,
  Inputs,
  Spinner,
} from "../../../../../../../Components";
import { useTranslation } from "react-i18next";
import { ApplicationUserSearch } from "../../../../../../../Services/userServices";
import {
  getDownloadableLink,
  showError,
  showSuccess,
} from "../../../../../../../Helper";
import * as Yup from "yup";
import { MultipleTypesDocumentsUploader } from "../../../../../../../SharedComponents";
import { ButtonBase, DialogActions } from "@material-ui/core";
import moment from "moment";
import { UpdateAward } from "../../../../../../../Services/AwardServices";

function EditUserAward({
  isOpen,
  activeAward,
  translationPath,
  setIsEditDialogOpen,
  refetchData,
}) {
  const { t } = useTranslation(translationPath);
  const reducer = useCallback((state, action) => {
    if (action.id !== "edit") return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const searchTimer = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useReducer(reducer, {
    awardName: null,
    awardDate: null,
    awardDocument: [],
    supportingDocuments: [],
  });

  const unitValidationSchema = Yup.object().shape({
    awardName: Yup.string().required(t("award-name-is-required")),
    awardDate: Yup.date()
      .default(() => new Date())
      .required(t("award-date-is-required")),
  });

  async function getCurrentUser() {
    const res = await ApplicationUserSearch({
      name: activeAward.userName || "",
      pageIndex: 1,
      pageSize: 50,
    });

    if (!(res && res.status && res.status !== 200)) {
      const newValue = res?.result?.filter((item) => {
        return item.applicationUserId === activeAward?.usersId;
      });
      setState({ id: "userData", value: newValue[0] });
    } else {
      showError(t("error-fetching-users"));
    }
  }

  async function onEditClicked() {
    setIsLoading(true);
    try {
      await unitValidationSchema.validate(state, { abortEarly: false });
      const documentsBuffer = [
        ...state?.awardDocument,
        ...state?.supportingDocuments,
      ].map((item) => ({
        documentName: item.documentName || item.fileName || null,
        documentLink: item.documentLink || item.url,
        fileId: item.fileId || item.uuid,
        isCertificate: item.isCertificate,
      }));
      const body = {
        awardName: state.awardName,
        awardDate: state.awardDate,
        usersId: state?.userData?.applicationUserId || state?.userData?.usersId,
        awardDocuments: documentsBuffer || [],
      };

      const res = await UpdateAward(activeAward.awardId, body);
      if (!(res && res.status && res.status !== 200)) {
        showSuccess(t("award-updated-successfully"));
        await refetchData();
        setIsEditDialogOpen(false);
      } else {
        showError(t("error-occurred-while-updating-award"));
      }
    } catch (error) {
      console.warn("error:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getCurrentUser();
    if (activeAward) {
      const awardDocument =
        activeAward?.awardDocuments
          ?.filter((item) => {
            return item.isCertificate === true;
          })
          .map((item) => ({
            awardDocumentId: item.awardDocumentId,
            uuid: item.fileId || item.uuid,
            fileName: item.documentName || item.fileName,
            documentLink: item.documentLink || item.url,
            isCertificate: item.isCertificate,
          })) || [];

      const awardSupportingDocuments =
        activeAward?.awardDocuments
          ?.filter((item) => {
            return item.isCertificate === false;
          })
          .map((item) => ({
            awardDocumentId: item.awardDocumentId,
            uuid: item.fileId || item.uuid,
            fileName: item.documentName || item.fileName,
            documentLink: item.documentLink || item.url,
            isCertificate: item.isCertificate,
          })) || [];

      setState({
        id: "edit",
        value: {
          ...state,
          awardName: activeAward.awardName,
          awardDate: activeAward.awardDate,
          awardDocument: awardDocument,
          supportingDocuments: awardSupportingDocuments,
        },
      });
    }
  }, []);

  return (
    <>
      <Spinner isActive={isLoading} isAbsolute />
      <DialogComponent
        isOpen={isOpen}
        titleText={t(`edit-award`)}
        maxWidth="sm"
        dialogContent={
          <>
            <div className="create-user-award-dialog-wrapper">
              <div className="select-user-section-container">
                <AutocompleteComponent
                  idRef="selectUserForAwardRef"
                  labelValue={t("select-user")}
                  labelClasses="Requierd-Color"
                  selectedValues={state?.userData || null}
                  data={[]}
                  displayLabel={(option) => option?.fullName || ""}
                  renderOption={(option) => option?.fullName || ""}
                  withoutSearchButton
                  multiple={false}
                  parentTranslationPath={translationPath}
                  translationPath={""}
                  isDisabled={true}
                  onChange={(e, newValue) => {}}
                  onInputKeyUp={(e) => {}}
                />
              </div>

              <Inputs
                idRef={"awardNameRef"}
                labelValue={"award-name"}
                type={"text"}
                labelClasses="Requierd-Color"
                value={state.awardName || ""}
                onInputChanged={(e) => {
                  const value = e.target.value ?? null;
                  setState({ id: "awardName", value: value });
                }}
                translationPath={""}
                parentTranslationPath={translationPath}
              />

              <DatePickerComponent
                type="date"
                labelValue="award-date"
                value={state.awardDate || null}
                labelClasses="Requierd-Color"
                onDateChanged={(value) => {
                  const newValue = !value
                    ? null
                    : moment(value).format("YYYY-MM-DDTHH:mm:ss");

                  setState({ id: "awardDate", value: newValue });
                }}
                translationPath={""}
                parentTranslationPath={translationPath}
              />

              <MultipleTypesDocumentsUploader
                idRef="awardCertificateUploaderRef"
                initUploadedFiles={state?.awardDocument || []}
                labelValue="award-certificate"
                accept="application/pdf, image/png, image/jpg"
                uploadedChanged={(files) => {
                  const newFiles = files.map((item) => {
                    return {
                      documentName: item.fileName || item.documentName || null,
                      fileId: item.uuid || item.fileId || null,
                      documentLink: item.url || item.documentLink || null,
                      isCertificate: true,
                    };
                  });

                  setState({
                    id: "awardDocument",
                    value: newFiles,
                  });
                }}
                multiple={false}
                isOpenGallery
                viewUploadedFilesCount={false}
                openGallery
                isDocuments
                chipHandler={(value) => () => {
                  const link = document.createElement("a");
                  link.setAttribute(
                    "download",
                    value.fileName || value.documentName
                  );
                  link.href = getDownloadableLink(value.uuid || value.fileId);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
                translationPath={""}
                parentTranslationPath={translationPath}
              />

              <MultipleTypesDocumentsUploader
                idRef="awardCertificateUploaderRef"
                labelValue="supporting-documents"
                initUploadedFiles={state?.supportingDocuments || []}
                multiple={true}
                isOpenGallery
                viewUploadedFilesCount={false}
                openGallery
                isDocuments
                uploadedChanged={(files) => {
                  const newFiles = files.map((item) => {
                    return {
                      documentName: item.fileName || item.documentName || null,
                      fileId: item.uuid || item.fileId || null,
                      documentLink: item.url || item.documentLink || null,
                      isCertificate: false,
                    };
                  });

                  setState({
                    id: "supportingDocuments",
                    value: newFiles,
                  });
                }}
                chipHandler={(value) => () => {
                  const link = document.createElement("a");
                  link.setAttribute(
                    "download",
                    value.fileName || value.documentName
                  );
                  link.href = getDownloadableLink(value.Zz || value.fileId);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
                translationPath={""}
                parentTranslationPath={translationPath}
              />
            </div>

            <DialogActions>
              <ButtonBase
                onClick={() => {
                  setIsEditDialogOpen(false);
                }}
                className={`btns theme-solid bg-cancel`}
              >
                {t(`cancel`)}
              </ButtonBase>
              <ButtonBase
                className={`btns theme-solid`}
                onClick={() => {
                  onEditClicked();
                }}
              >
                {t(`edit`)}
              </ButtonBase>
            </DialogActions>
          </>
        }
        translationPath={""}
        parentTranslationPath={translationPath}
      />
    </>
  );
}

export default EditUserAward;
